import { serverNow } from './serverClock';
import { Auction, AuctionLot } from '../types/auction.types';

/**
 * Robustly coerce any date-ish value into a Date (or null).
 *
 * Lot end-times arrive from Firestore as Timestamp objects
 * ({ seconds, nanoseconds }, with a .toDate() method), but they can also be a
 * JS Date (already converted), an ISO string, or an epoch number. The old
 * `x instanceof Date ? x : new Date(x)` pattern produced an "Invalid Date" for
 * Timestamps → NaN timeRemaining → soft-close never fired. This normalizes all
 * of those shapes and returns null when the value is missing/unparseable so
 * callers can guard instead of propagating NaN.
 */
export const toDate = (value: unknown): Date | null => {
  if (value == null) return null;
  if (value instanceof Date) {
    return isNaN(value.getTime()) ? null : value;
  }
  // Firestore Timestamp (web + admin SDKs both expose toDate()).
  if (typeof (value as any).toDate === 'function') {
    const d = (value as any).toDate();
    return d instanceof Date && !isNaN(d.getTime()) ? d : null;
  }
  // Plain { seconds, nanoseconds } shape (serialized Timestamp).
  if (typeof (value as any).seconds === 'number') {
    const v = value as any;
    const ms = v.seconds * 1000 + (typeof v.nanoseconds === 'number' ? v.nanoseconds / 1e6 : 0);
    const d = new Date(ms);
    return isNaN(d.getTime()) ? null : d;
  }
  if (typeof value === 'number' || typeof value === 'string') {
    const d = new Date(value);
    return isNaN(d.getTime()) ? null : d;
  }
  return null;
};

/**
 * Default soft-close behavior for the WhiteLabelBuddy auction protocol.
 *
 * SHOP_CLEANOUT_050326 spec (Sunday May 3, 2026 — HiBid parallel test):
 *   - 3-minute soft-close window (a bid in the last 180s extends)
 *   - 3-minute extension on each qualifying bid
 *   - 0-second cooldown — unlimited consecutive extensions, matching HiBid
 *   - 10-second stagger between lot closes
 *
 * Per-auction overrides on the Auction document still take precedence.
 */
export const DEFAULT_STAGGER_SECONDS = 10;

/**
 * THE soft-close defaults. Every surface reads these — the countdown, the bid
 * modal, the go-live dialog, and (mirrored, see below) the Cloud Function.
 *
 * They used to be four different numbers in four files: the server fell back to
 * 180/180, GoLiveDialog wrote 900/60, CountdownTimer displayed a 900s window and
 * BidFromTileModal a 180s one. A bidder could be shown a 15-minute "closing
 * soon" state while the server would only extend inside 3 minutes — the page
 * promising something the server would not honour. One definition now.
 *
 * WINDOW is 120s, not 900s. A 15-minute window on a sale whose lots span 17
 * minutes means every bid extends, which is not a soft close.
 *
 * EXTENSION (180s) is deliberately LONGER than the window. After an extension
 * the lot has 180s left, which is outside the 120s window, so the next bid does
 * not automatically extend again — a bidder has to wait to re-enter the window.
 * Keep that relationship if you change these: extension <= window means every
 * extension lands back inside the window and the clock is self-sustaining.
 */
export const DEFAULT_SOFT_CLOSE_WINDOW_SECONDS = 120;
export const DEFAULT_SOFT_CLOSE_EXTENSION_SECONDS = 180;
export const DEFAULT_EXTENSION_COOLDOWN_SECONDS = 0;

/**
 * THE BACKSTOP. No lot may close later than its scheduled end plus this.
 *
 * Soft close on its own has no upper bound: each qualifying bid pushes the end
 * out, nothing counts the pushes, and a determined pair of bidders can hold a
 * lot open indefinitely. That is not a tuning problem — no combination of window
 * and extension makes the end time bounded, because the bidder controls how many
 * times the rule fires. Only a cap does.
 *
 * Per-auction override: auction.maxExtensionSeconds. There is deliberately NO
 * value meaning "unlimited" — 0 means a lot never runs past its scheduled close.
 */
export const DEFAULT_MAX_EXTENSION_SECONDS = 1800; // 30 minutes

// Debug logging for timing math is OFF by default. isInSoftCloseWindow runs on
// every render tick (per lot), so its log flooded the console — gate ALL timing
// logs behind this flag. Flip to true only when debugging soft-close locally.
const DEBUG_TIMING = false;

/**
 * Calculate scheduled end time for a lot based on its position
 */
export const calculateLotEndTime = (
  auctionEndTime: Date,
  lotIndex: number,
  staggerSeconds: number = DEFAULT_STAGGER_SECONDS
): Date => {
  const endTime = new Date(toDate(auctionEndTime) ?? auctionEndTime);
  endTime.setSeconds(endTime.getSeconds() + (lotIndex * staggerSeconds));
  return endTime;
};

/**
 * Calculate end times for all lots in an auction
 */
export const calculateAllLotEndTimes = (
  auction: Auction
): AuctionLot[] => {
  const staggerTime = auction.staggerTime ?? DEFAULT_STAGGER_SECONDS;

  return auction.lots.map((lot, index) => ({
    ...lot,
    scheduledEndTime: calculateLotEndTime(auction.endDate, index, staggerTime),
    actualEndTime: lot.actualEndTime || calculateLotEndTime(auction.endDate, index, staggerTime),
    extensionCount: lot.extensionCount || 0,
  }));
};

/**
 * Check if a lot is in soft close window
 */
export const isInSoftCloseWindow = (
  lotEndTime: Date,
  softCloseWindowSeconds: number = DEFAULT_SOFT_CLOSE_WINDOW_SECONDS
): boolean => {
  // Normalize any Firestore Timestamp / Date / string / number end-time.
  const endTimeDate = toDate(lotEndTime);
  if (!endTimeDate) {
    // Unparseable / missing end time — cannot be inside the soft-close window.
    return false;
  }

  const now = new Date(serverNow()); // server-corrected display clock (utils/serverClock)
  const timeRemaining = endTimeDate.getTime() - now.getTime();
  const windowMs = softCloseWindowSeconds * 1000;

  if (DEBUG_TIMING) console.log('⏰ Soft close check:', {
    endTime: endTimeDate.toLocaleTimeString(),
    timeRemaining: Math.floor(timeRemaining / 1000),
    windowSeconds: softCloseWindowSeconds,
    inWindow: timeRemaining > 0 && timeRemaining <= windowMs,
  });
  
  return timeRemaining > 0 && timeRemaining <= windowMs;
};

/** What the soft-close rule decided, and why. `reason` is for logs and tests. */
export type SoftCloseReason =
  | 'no-end-time'      // lot has neither actualEndTime nor scheduledEndTime
  | 'not-in-window'    // bid landed outside the soft-close window
  | 'at-cap'           // already at the backstop; the clock does not move again
  | 'extended'         // normal extension
  | 'extended-clamped';// extension truncated so it lands exactly on the backstop

export interface SoftCloseDecision {
  extend: boolean;
  /** The end time to write. Null when extend is false. */
  nextEndTime: Date | null;
  /** True when the backstop, not the extension, decided the value. */
  capped: boolean;
  reason: SoftCloseReason;
  /** The latest this lot may ever close. Null only when no base could be found. */
  capAt: Date | null;
  /**
   * Set ONLY on a lot that has no scheduledEndTime and is being extended for the
   * first time. Persist it as lot.extensionBaseEndTime so later extensions still
   * have a fixed origin to measure the cap from. Without it the cap would be
   * measured from an end time that the extensions themselves keep moving, which
   * is how a cap silently becomes no cap.
   */
  baseToPersist: Date | null;
}

/**
 * THE soft-close decision. Pure: no clock, no I/O — `nowMs` is passed in.
 *
 * MIRRORED byte-for-byte in functions/src/index.ts as decideSoftClose(), because
 * functions/ is a separate build and cannot import from the CRA app. The two
 * copies are held together by src/__verify__/softCloseBackstop.test.ts, which
 * loads the function file from disk and runs both implementations over the same
 * table. If you edit one and not the other, that test fails.
 */
export const decideSoftClose = (input: {
  scheduledEndTime?: unknown;
  actualEndTime?: unknown;
  extensionBaseEndTime?: unknown;
  extensionCount?: number;
  nowMs: number;
  windowSeconds?: number;
  extensionSeconds?: number;
  maxExtensionSeconds?: number;
}): SoftCloseDecision => {
  const num = (v: unknown, dflt: number): number => {
    // null / undefined / '' mean ABSENT, not zero. Number(null) is 0, so the
    // naive version read a nulled maxExtensionSeconds as "never extend past the
    // scheduled close" and silently disabled soft close for the whole sale.
    // An explicit numeric 0 still means exactly that — but only when written.
    if (v === null || v === undefined || v === '') return dflt;
    const n = Number(v);
    return Number.isFinite(n) && n >= 0 ? n : dflt;
  };
  const windowSec = num(input.windowSeconds, DEFAULT_SOFT_CLOSE_WINDOW_SECONDS);
  const extSec = num(input.extensionSeconds, DEFAULT_SOFT_CLOSE_EXTENSION_SECONDS);
  const maxSec = num(input.maxExtensionSeconds, DEFAULT_MAX_EXTENSION_SECONDS);

  const none = (reason: SoftCloseReason, capAt: Date | null = null): SoftCloseDecision =>
    ({ extend: false, nextEndTime: null, capped: false, reason, capAt, baseToPersist: null });

  const end = toDate(input.actualEndTime) ?? toDate(input.scheduledEndTime);
  if (!end) return none('no-end-time');

  const remaining = end.getTime() - input.nowMs;
  if (!(remaining > 0 && remaining <= windowSec * 1000)) return none('not-in-window');

  // The cap is measured from a FIXED origin. scheduledEndTime is that origin
  // whenever it exists; a lot that lacks one gets its pre-extension end frozen
  // as extensionBaseEndTime on the first extension.
  const scheduled = toDate(input.scheduledEndTime);
  const frozen = toDate(input.extensionBaseEndTime);
  const firstExtension = !(Number(input.extensionCount) > 0);
  const base = scheduled ?? frozen ?? (firstExtension ? end : null);
  if (!base) return none('no-end-time');

  const capAt = new Date(base.getTime() + maxSec * 1000);
  const baseToPersist = !scheduled && !frozen ? base : null;

  if (end.getTime() >= capAt.getTime()) {
    return { extend: false, nextEndTime: null, capped: true, reason: 'at-cap', capAt, baseToPersist: null };
  }

  const proposed = new Date(end.getTime() + extSec * 1000);
  if (proposed.getTime() > capAt.getTime()) {
    return { extend: true, nextEndTime: capAt, capped: true, reason: 'extended-clamped', capAt, baseToPersist };
  }
  return { extend: true, nextEndTime: proposed, capped: false, reason: 'extended', capAt, baseToPersist };
};

/**
 * Calculate new end time after soft close extension
 */
export const extendLotEndTime = (
  currentEndTime: Date,
  extensionSeconds: number = DEFAULT_SOFT_CLOSE_EXTENSION_SECONDS
): Date => {
  const base = toDate(currentEndTime) ?? new Date();
  const newEndTime = new Date(base);
  newEndTime.setSeconds(newEndTime.getSeconds() + extensionSeconds);
  return newEndTime;
};

/**
 * Check if lot should be extended (bid in soft close window + not recently extended)
 *
 * Cooldown defaults to 0 — unlimited consecutive extensions, matching HiBid.
 * Pass a non-zero cooldownSeconds explicitly if a future auction needs throttling.
 */
export const shouldExtendLot = (
  lot: AuctionLot,
  softCloseWindowSeconds: number = DEFAULT_SOFT_CLOSE_WINDOW_SECONDS,
  cooldownSeconds: number = DEFAULT_EXTENSION_COOLDOWN_SECONDS
): boolean => {
  // Use actualEndTime if it exists (lot has been extended), otherwise use scheduledEndTime
  const endTime = lot.actualEndTime || lot.scheduledEndTime;
  
  if (!endTime) {
    if (DEBUG_TIMING) console.log('❌ No end time found for lot', lot.lotNumber);
    return false;
  }
  
  if (DEBUG_TIMING) console.log('🔍 Checking if lot should extend:', {
    lotNumber: lot.lotNumber,
    endTime: toDate(endTime)?.toLocaleTimeString() ?? 'unparseable',
    hasActualEndTime: !!lot.actualEndTime,
    hasScheduledEndTime: !!lot.scheduledEndTime,
  });
  
  // Check if in soft close window
  if (!isInSoftCloseWindow(endTime, softCloseWindowSeconds)) {
    if (DEBUG_TIMING) console.log('❌ Not in soft close window');
    return false;
  }
  
  // Check cooldown - don't extend if recently extended
  const lastExtension = toDate(lot.lastExtensionAt);
  if (lastExtension) {
    const timeSinceExtension = Date.now() - lastExtension.getTime();
    if (timeSinceExtension < cooldownSeconds * 1000) {
      if (DEBUG_TIMING) console.log('❌ Cooldown active:', Math.floor(timeSinceExtension / 1000), 'seconds since last extension');
      return false;
    }
  }

  if (DEBUG_TIMING) console.log('✅ Lot should be extended!');
  return true;
};

/**
 * Get time remaining in seconds
 */
export const getTimeRemaining = (endTime: Date): number => {
  const end = toDate(endTime);
  if (!end) return 0;
  const now = new Date(serverNow()); // server-corrected display clock (utils/serverClock)
  const remaining = end.getTime() - now.getTime();
  return Math.max(0, Math.floor(remaining / 1000));
};

/**
 * Format time remaining as string (HH:MM:SS or MM:SS)
 */
export const formatTimeRemaining = (seconds: number): string => {
  if (seconds <= 0) return '00:00';

  const pad = (n: number) => n.toString().padStart(2, '0');
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;

  // >= 24h: day-aware "Nd HH:MM:SS" (e.g. "7d 05:31:10").
  if (days >= 1) {
    return `${days}d ${pad(hours)}:${pad(minutes)}:${pad(secs)}`;
  }
  // Under 24h: keep HH:MM:SS (or M:SS under an hour).
  if (hours > 0) {
    return `${hours}:${pad(minutes)}:${pad(secs)}`;
  }
  return `${minutes}:${pad(secs)}`;
};
